//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { Mixins } from '@theme/mixins';

interface Props {
  query: string;
  onSelect: (data: string) => void;
  labels: string[];
}

export const LabelTypeahead = ({ labels, onSelect, query }: Props) => {
  // This function renders a label that will have the query (case insensitive) in bold within it
  const renderLabel = (label: string) => {
    const indices: number[] = [];
    let searchLabel = label.toLocaleLowerCase();
    const lowerCaseQuery = query.toLocaleLowerCase();

    // Create an array of the indices within the label that should be in bold
    while (searchLabel.includes(lowerCaseQuery) && lowerCaseQuery !== '') {
      const index = searchLabel.indexOf(lowerCaseQuery);
      const indexInOriginalLabel = label.length - searchLabel.length + index;

      // Set the searchLabel to only after the currently found substring
      searchLabel = searchLabel.substring(
        index + lowerCaseQuery.length,
        searchLabel.length,
      );

      // Add the index that the substring was found and then for the length of the query, add each
      // index after where the substring was found
      for (
        let i = indexInOriginalLabel;
        i < indexInOriginalLabel + query.length;
        i++
      ) {
        indices.push(i);
      }
    }

    // Bold the characters that are at the indices in the label
    return label.split('').map((char, index) => {
      return indices.includes(index) ? (
        <QueryHighlight>{char}</QueryHighlight>
      ) : (
        <span key={index}>{char}</span>
      );
    });
  };

  const renderLabelButton = (label: string, index: number) => {
    return (
      <LabelSuggestion key={index} onClick={() => onSelect(label)}>
        {renderLabel(label)}
      </LabelSuggestion>
    );
  };

  return labels.length !== 0 ? (
    <TypeaheadContainer>
      {labels.map((val, index) => renderLabelButton(val, index))}
    </TypeaheadContainer>
  ) : null;
};

const LabelSuggestion = styled.div(
  ({ theme }) => `
  ${Mixins.Text.Body};
  color: ${theme.colors.gray8};
  text-align: left;
  padding: ${theme.spacings[2]};
  border-radius: ${theme.spacings[1]};
  &:hover {
    background-color: ${theme.colors.gray0};
  }
  cursor: pointer;
`,
);

const TypeaheadContainer = styled.div`
  z-index: 1000;
  width: 100%;
  position: absolute;
  border-radius: ${({ theme }) => theme.spacings[1]};
  max-height: 180px;
  overflow-x: hidden;
  overflow-y: auto;
  text-overflow: ellipsis;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.gray1};
  box-shadow: none;
  top: 105%;
  filter: drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.24));
`;

const QueryHighlight = styled.span`
  ${Mixins.Text.BodyBold};
`;
